import { useState, useEffect } from 'react';

const PASSWORD = 'flowglow2026';
const STORAGE_KEY = 'fg_access_granted';

export default function PasswordGate({ children }: { children: React.ReactNode }) {
  const [unlocked, setUnlocked] = useState(false);
  const [input, setInput] = useState('');
  const [error, setError] = useState(false);
  const [shake, setShake] = useState(false);
  const [checking, setChecking] = useState(true);

  useEffect(() => {
    const granted = localStorage.getItem(STORAGE_KEY);
    if (granted === 'true') setUnlocked(true);
    setChecking(false);
  }, []);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (input === PASSWORD) {
      localStorage.setItem(STORAGE_KEY, 'true');
      setUnlocked(true);
      setError(false);
    } else {
      setError(true);
      setShake(true);
      setInput('');
      setTimeout(() => setShake(false), 600);
    }
  };

  if (checking) return null;
  if (unlocked) return <>{children}</>;

  return (
    <div className="min-h-screen bg-[#faf8f5] flex flex-col items-center justify-center px-4">
      {/* Background decoration */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <div className="absolute -top-32 -right-32 w-96 h-96 rounded-full bg-[#e8d5c4]/30" />
        <div className="absolute -bottom-32 -left-32 w-96 h-96 rounded-full bg-[#d4c5b5]/20" />
      </div>

      <div className="relative z-10 w-full max-w-md">
        {/* Logo / Brand */}
        <div className="text-center mb-10">
          <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-[#c9a882]/15 mb-5">
            <i className="ri-lock-2-line text-2xl text-[#c9a882]" />
          </div>
          <h1 className="font-serif text-3xl text-[#2c2c2c] tracking-wide mb-1">Flow <span className="text-[#d4a08a]">&amp;</span> Glow</h1>
          <p className="text-sm text-[#8a7a6a] tracking-widest uppercase">Köln · Kosmetische Zahnaufhellung</p>
        </div>

        {/* Card */}
        <div className="bg-white rounded-2xl p-8 border border-[#e8ddd4]">
          <h2 className="text-lg font-semibold text-[#2c2c2c] mb-1">Diese Seite ist geschützt</h2>
          <p className="text-sm text-[#8a7a6a] mb-6 leading-relaxed">
            Die Website befindet sich aktuell in der Überarbeitung. Bitte gib das Passwort ein, um fortzufahren.
          </p>

          <form onSubmit={handleSubmit} data-readdy-form>
            <div className="mb-4">
              <label htmlFor="password" className="block text-xs font-medium text-[#5a4a3a] uppercase tracking-wider mb-2">
                Passwort
              </label>
              <div
                className={`relative transition-all duration-300 ${shake ? 'animate-[shake_0.5s_ease-in-out]' : ''}`}
                style={shake ? { animation: 'shake 0.5s ease-in-out' } : {}}
              >
                <input
                  id="password"
                  type="password"
                  name="password"
                  value={input}
                  onChange={(e) => { setInput(e.target.value); setError(false); }}
                  placeholder="Passwort eingeben"
                  className={`w-full px-4 py-3 rounded-lg border text-sm text-[#2c2c2c] bg-[#faf8f5] outline-none transition-all
                    ${error
                      ? 'border-red-300 focus:border-red-400 focus:ring-2 focus:ring-red-100'
                      : 'border-[#e0d5c8] focus:border-[#c9a882] focus:ring-2 focus:ring-[#c9a882]/20'
                    }`}
                  autoFocus
                />
                <div className="absolute right-3 top-1/2 -translate-y-1/2 w-5 h-5 flex items-center justify-center">
                  <i className={`ri-key-2-line text-base ${error ? 'text-red-400' : 'text-[#c9a882]'}`} />
                </div>
              </div>
              {error && (
                <p className="mt-2 text-xs text-red-500 flex items-center gap-1">
                  <i className="ri-error-warning-line" />
                  Falsches Passwort. Bitte versuche es erneut.
                </p>
              )}
            </div>

            <button
              type="submit"
              className="w-full py-3 rounded-lg bg-[#c9a882] hover:bg-[#b8956f] text-white text-sm font-medium tracking-wide transition-colors duration-200 cursor-pointer whitespace-nowrap"
            >
              Zugang bestätigen
            </button>
          </form>
        </div>

        {/* Footer note */}
        <p className="text-center text-xs text-[#b0a090] mt-6">
          Du bist Kund*in?{' '}
          <a href="mailto:hallo@flowundglow.de" className="underline hover:text-[#c9a882] transition-colors">
            Schreib uns gerne
          </a>
        </p>
      </div>

      <style>{`
        @keyframes shake {
          0%, 100% { transform: translateX(0); }
          15% { transform: translateX(-8px); }
          30% { transform: translateX(8px); }
          45% { transform: translateX(-6px); }
          60% { transform: translateX(6px); }
          75% { transform: translateX(-3px); }
          90% { transform: translateX(3px); }
        }
      `}</style>
    </div>
  );
}
